import { dollars, from } from '@nuts/auto-delivery-sdk/dist/utils/money';
import pick from 'lodash/pick';
import type { CamelCasedProperties } from 'type-fest';

import { getMoneyValue } from '@/lib/product/prices';
import { ProductAdded, productAdded } from '@/rudder-typer';
import analytics, { gtag } from '@/utils/analytics';
import {
  formatListSourceAttribution,
  ListSourceAttribution,
  ListSourceAttributionFields,
} from '@/utils/analytics/listSourceAttribution';
import { PageSection } from '@/utils/analytics/PageSection';
import { Taggstar } from '@/utils/analytics/taggstar';
import { computeSavings, Money } from '@/utils/money';
import { toLegacySku } from '@/utils/product';
import { ProductCardData } from '@/utils/productCard';
import { NutsProductProjection } from '@/utils/productProjection';
import { reportError } from '@/utils/reportError';

export interface NutsAddToCartItem
  extends Omit<
    CamelCasedProperties<ProductAdded>,
    'pageSection' | 'price' | ListSourceAttributionFields
  > {
  /**  interval in days */
  readonly autodeliveryInterval?: number;
  readonly cost?: Money;
  readonly discount?: Money;
  /** Algolia index that served query results including this product */
  readonly indexName?: string;
  readonly itemListName?: string;
  readonly listSourceAttribution?: ListSourceAttribution;
  readonly markedAsGift?: boolean;
  readonly pageSection?: PageSection<
    | 'Above-the-Fold Recommendations'
    | 'Added to Auto-Delivery Modal'
    | 'Added to Cart Modal'
    | 'Buy Block'
    | 'Mini PDP'
    | 'Product List'
    | 'Product Recommendations'
    | 'Size Selector Modal'
  >;
  readonly price: Money;
  readonly priceBeforeDiscount: Money;
  /** Algolia query identifier for Dynamic Re-ranking conversion events */
  readonly searchQueryID?: string;
  readonly variant?: string;
  readonly variantLabel?: string;
  readonly weight?: number;
}

interface GtmProduct {
  id: string;
  itemExternalId: string;
  name: string;
  price: number | string;
  quantity: number;
}

export interface GtmAddToCartPayload {
  ecommerce: {
    add: {
      actionField: {
        list: string;
      };
      products: GtmProduct[];
    };
    currencyCode: string;
  };
}

function formatGtmAddToCartPayload(eventProps: NutsAddToCartItem): GtmAddToCartPayload {
  const quantity = eventProps.quantity || 1;
  const payload: GtmAddToCartPayload = {
    ecommerce: {
      add: {
        actionField: {
          list: eventProps.itemListName ?? '',
        },
        products: [
          {
            id: toLegacySku(eventProps.sku),
            itemExternalId: eventProps.productId ?? '',
            name: eventProps.name ?? '',
            price: String(dollars(Money.multiply(eventProps.price, quantity))),
            quantity,
          },
        ],
      },
      currencyCode: 'USD',
    },
  };

  return payload;
}

function formatGTagAddToCartPayload(eventProps: NutsAddToCartItem, preSubstitutionSku?: string) {
  const quantity = eventProps.quantity || 1;
  const sku = preSubstitutionSku ?? eventProps.sku;
  const gtagEvent: Record<string, unknown> = {
    currency: 'USD',
    items: [
      {
        autodelivery_interval: eventProps.autodeliveryInterval,
        cost: eventProps.cost ? dollars(eventProps.cost) : undefined,
        coupon: eventProps.coupon,
        discount: eventProps.discount ? dollars(eventProps.discount) : undefined,
        index: eventProps.position,
        indexName: eventProps.indexName,
        item_id: eventProps.productId,
        item_list_name: eventProps.itemListName,
        item_name: eventProps.name,
        item_variant_name: eventProps.variant,
        item_variant: sku,
        marked_as_gift: eventProps.markedAsGift,
        price_before_discount: dollars(eventProps.priceBeforeDiscount),
        price: dollars(eventProps.price),
        quantity: eventProps.quantity,
        reporting_category: eventProps.reportingCategory,
        searchQueryID: eventProps.searchQueryID,
        weight: eventProps.weight,
      },
    ],
    value: dollars(Money.multiply(eventProps.price, quantity)),
  };
  return gtagEvent;
}

function pickProductAdded(nutsAddToCartItem: NutsAddToCartItem): ProductAdded {
  const properties = pick(nutsAddToCartItem, [
    'coupon',
    'name',
    'position',
    'price',
    'autodeliveryInterval',
    'quantity',
    'sku',
    'variant',
  ]);
  return {
    ...properties,
    ...formatListSourceAttribution(nutsAddToCartItem.listSourceAttribution),
    bundle_relation: nutsAddToCartItem.bundleRelation,
    bundle_sku: nutsAddToCartItem.bundleSku,
    cart_id: nutsAddToCartItem.cartId,
    page_section: nutsAddToCartItem.pageSection,
    price: dollars(nutsAddToCartItem.price),
    product_id: nutsAddToCartItem.productId,
    product_subscription_interval: properties.autodeliveryInterval,
    reporting_category: nutsAddToCartItem.reportingCategory,
    variant_label: nutsAddToCartItem.variantLabel,
  };
}

export function formatNutsAddToCartItemFromProductCard(
  productCard: ProductCardData,
  options: {
    cartId?: string;
    indexName?: string;
    itemListName?: string;
    listSourceAttribution?: ListSourceAttribution;
    markedAsGift?: boolean;
    pageSection: NutsAddToCartItem['pageSection'];
    position?: number;
    quantity: number;
  },
): NutsAddToCartItem {
  return {
    cost: Money.fromString(productCard.cost),
    coupon: productCard.totalSavings?.description?.en,
    discount: productCard.totalSavings?.value ?? from(0),
    name: productCard.name,
    price: productCard.piecePrice,
    priceBeforeDiscount: productCard.listPrice.value,
    productId: productCard.productKey,
    reportingCategory: productCard.reportingCategory,
    searchQueryID: productCard.searchQueryId,
    sku: productCard.sku,
    variant: productCard.unitName,
    variantLabel: productCard.variantLabel,
    weight: productCard.weight ? Number(productCard.weight) : 0,
    ...options,
  };
}

export function formatNutsAddToCartItemFromProductProjection(
  productProjection: NutsProductProjection,
  selectedSku: string,
  options: {
    cartId?: string;
    indexName?: string;
    itemListName?: string;
    listSourceAttribution?: ListSourceAttribution;
    markedAsGift?: boolean;
    pageSection: NutsAddToCartItem['pageSection'];
    position?: number;
    quantity: number;
  },
): NutsAddToCartItem {
  const addedItem = NutsProductProjection.toDisplayVariant(productProjection, {
    sku: selectedSku,
  });
  const quantity = options.quantity ?? 1;
  const savings = computeSavings(
    addedItem.variant.listPrice.value,
    getMoneyValue(addedItem.variant.listPrice, quantity),
  );

  return {
    cost: addedItem.variant.pieceCost ?? from(0),
    coupon: addedItem.variant.listPrice.discounted?.discount.description ?? undefined,
    discount: savings.value,
    name: addedItem.name,
    price: getMoneyValue(addedItem.variant.listPrice, quantity),
    priceBeforeDiscount: addedItem.variant.listPrice.value,
    productId: addedItem.productKey,
    reportingCategory: productProjection.reportingCategory?.key,
    sku: addedItem.variant.sku,
    variant: addedItem.variant.variantName?.en,
    variantLabel: addedItem.variant.variantLabel,
    weight: addedItem.variant.weight,
    ...options,
  };
}

export function sendProductAddedEvent(
  nutsAddToCartItem: NutsAddToCartItem,
  preSubstitutionSku?: string,
) {
  const gtmEventObj = formatGtmAddToCartPayload(nutsAddToCartItem);
  analytics.sendEvent('addToCart', '', gtmEventObj);

  const googleEventObj = formatGTagAddToCartPayload(nutsAddToCartItem, preSubstitutionSku);
  gtag('event', 'add_to_cart', googleEventObj);

  Taggstar.addToBag({
    id: nutsAddToCartItem.productId,
    quantity: nutsAddToCartItem.quantity,
  });

  try {
    const productAddedPayload = pickProductAdded(nutsAddToCartItem);
    productAdded(productAddedPayload);
  } catch (e) {
    reportError(e);
  }
}
